'use client';

/**
 * LoginPageBenefits — capability cards.
 *
 * Why use the SUM-hosted login over rolling your own: brand consistency,
 * no auth code to maintain, always-current security, and a two-level
 * security model that lets the operator pick filter-only or backend-validated.
 */

import React from 'react';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import { useTranslation } from '@/i18n/client';
import { HiAdjustmentsHorizontal, HiArrowPath, HiBolt, HiPaintBrush } from 'react-icons/hi2';

type Props = { lang: Locale };

type Key = 'brand' | 'no_code' | 'current' | 'two_levels';
type Copy = { title: string; body: string };

const ITEMS: { key: Key; Icon: typeof HiBolt }[] = [
  { key: 'brand', Icon: HiPaintBrush },
  { key: 'no_code', Icon: HiBolt },
  { key: 'current', Icon: HiArrowPath },
  { key: 'two_levels', Icon: HiAdjustmentsHorizontal },
];

const LoginPageBenefits = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const copyFor = (k: Key): Copy => ({
    title: t(`LoginPage/Benefits/${k}/title`),
    body: t(`LoginPage/Benefits/${k}/body`),
  });

  return (
    <section
      id="benefits"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0A0E27',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="02" label={t('LoginPage/Benefits/badge')} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {t('LoginPage/Benefits/title1')} <span style={{ color: '#B4C1D8' }}>{t('LoginPage/Benefits/title2')}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {t('LoginPage/Benefits/subtitle')}
        </p>

        <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          {ITEMS.map(({ key, Icon }) => {
            const c = copyFor(key);
            return (
              <article
                key={key}
                className="rounded-2xl p-6"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div
                  className="inline-flex items-center justify-center rounded-lg mb-4"
                  style={{
                    width: 40,
                    height: 40,
                    background: 'rgba(167,139,250,0.12)',
                    border: '1px solid rgba(167,139,250,0.30)',
                  }}
                >
                  <Icon style={{ width: 20, height: 20, color: '#A78BFA' }} />
                </div>
                <h3
                  className="font-semibold"
                  style={{
                    fontSize: 19,
                    color: '#F5F7FA',
                    margin: '0 0 8px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {c.title}
                </h3>
                <p
                  style={{
                    color: '#B4C1D8',
                    fontSize: 14,
                    lineHeight: 1.55,
                    margin: 0,
                  }}
                >
                  {c.body}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default LoginPageBenefits;
